import { Helmet } from "@dr.pogodin/react-helmet";
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import RecentPosts from "../components/RecentPosts";
import ExpenseTrackerBanner from "../components/ExpenseTrackerBanner";

export default function BreakEvenCalculator() {
  // W-2 inputs
  const [w2Salary, setW2Salary] = useState("75000");
  const [w2401k, setW2401k] = useState("5");
  const [healthInsurance, setHealthInsurance] = useState("6000");
  const [paidTimeOff, setPaidTimeOff] = useState("15");
  const [employerSSMatch, setEmployerSSMatch] = useState(true);
  
  // Freelance inputs
  const [desiredIncome, setDesiredIncome] = useState("75000");
  const [businessExpenses, setBusinessExpenses] = useState("10000");
  const [healthInsuranceFreelance, setHealthInsuranceFreelance] = useState("8000");
  const [retirementSavings, setRetirementSavings] = useState("7500");
  const [stateCode, setStateCode] = useState("CA");

  const results = useMemo(() => {
    const w2SalaryNum = parseFloat(w2Salary) || 0;
    const w2401kRate = (parseFloat(w2401k) || 0) / 100;
    const healthNum = parseFloat(healthInsurance) || 0;
    const ptoNum = parseFloat(paidTimeOff) || 0;
    
    const desiredNum = parseFloat(desiredIncome) || 0;
    const expensesNum = parseFloat(businessExpenses) || 0;
    const healthFreelanceNum = parseFloat(healthInsuranceFreelance) || 0;
    const retirementNum = parseFloat(retirementSavings) || 0;

    // W-2 Total Value
    const w2401kAmount = w2SalaryNum * w2401kRate;
    const ptoValue = (w2SalaryNum / 260) * ptoNum; // 260 working days
    const ssMatchValue = employerSSMatch ? (w2SalaryNum * 0.062) : 0; // Employer pays 6.2% SS
    const medicareMatchValue = employerSSMatch ? (w2SalaryNum * 0.0145) : 0; // Employer pays 1.45% Medicare
    
    const totalW2Value = w2SalaryNum + w2401kAmount + healthNum + ptoValue + ssMatchValue + medicareMatchValue;

    // Freelance needed (before taxes)
    const seTaxRate = 0.153; // 15.3% self-employment tax
    const federalEstimate = 0.22; // Rough federal estimate (22% bracket)
    const stateEstimate = stateCode === "CA" ? 0.093 : 
                         stateCode === "NY" ? 0.065 :
                         stateCode === "TX" || stateCode === "FL" ? 0 :
                         0.05; // Generic estimate
    
    const combinedTaxRate = seTaxRate + federalEstimate + stateEstimate;

    // Freelance gross needed to net the desired income
    const grossForDesiredIncome = desiredNum / (1 - combinedTaxRate);
    
    // Add back business expenses (these reduce profit)
    // Add retirement savings (pre-tax deduction)
    // Add health insurance (deductible for self-employed)
    const totalFreelanceNeeded = grossForDesiredIncome + expensesNum;

    // Break-even analysis
    const freelanceAdvantage = totalFreelanceNeeded < totalW2Value;
    const difference = Math.abs(totalFreelanceNeeded - totalW2Value);

    // Monthly equivalents
    const monthlyW2 = totalW2Value / 12;
    const monthlyFreelance = totalFreelanceNeeded / 12;

    return {
      totalW2Value,
      w2Breakdown: {
        salary: w2SalaryNum,
        retirement: w2401kAmount,
        health: healthNum,
        pto: ptoValue,
        ssMatch: ssMatchValue,
        medicareMatch: medicareMatchValue
      },
      totalFreelanceNeeded,
      monthlyW2,
      monthlyFreelance,
      freelanceAdvantage,
      difference,
      combinedTaxRate
    };
  }, [w2Salary, w2401k, healthInsurance, paidTimeOff, employerSSMatch, desiredIncome, businessExpenses, healthInsuranceFreelance, retirementSavings, stateCode]);

  return (
    <>
      <Helmet>
        <title>W-2 vs Freelance Break-Even Calculator | Should I Go Self-Employed?</title>
        <meta
          name="description"
          content="Calculate how much you need to earn as a freelancer to match your W-2 employee benefits and salary. Factors in taxes, health insurance, retirement, and more."
        />
        <meta name="keywords" content="w2 vs 1099, freelance vs employee, should i quit my job, self employment calculator, break even calculator" />
        <link rel="canonical" href="https://free1099calc.com/tools/break-even-calculator" />
      </Helmet>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-amber-900 via-amber-800 to-slate-900 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1554224311-beee460c201f?w=1920&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-amber-900/85 to-slate-900/90" />
        
        <div className="relative max-w-6xl mx-auto px-4 py-20 md:py-28">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-block px-4 py-2 bg-amber-500/20 backdrop-blur-sm border border-amber-400/30 rounded-full mb-6">
              <span className="text-amber-300 text-sm font-semibold">⚖️ W-2 vs 1099</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Break-Even Calculator
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 mb-8">
              Calculate how much you need to earn as a freelancer to replace your W-2 salary and benefits.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#calculator"
                className="inline-block bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors shadow-lg"
              >
                Compare Income →
              </a>
              <Link
                to="/tools"
                className="inline-block bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white border border-white/30 px-8 py-4 rounded-lg font-semibold transition-colors"
              >
                More Tools
              </Link>
            </div>
          </div>
        </div>
      </div>

    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-12" id="calculator">

        {/* Expense Tracker Banner */}
        <div className="mb-8">
          <ExpenseTrackerBanner compact />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* W-2 inputs */}
          <div className="bg-white rounded-3xl border-2 border-blue-200 shadow-xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-xl">
                <svg className="w-7 h-7 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                  <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Your W-2 Job</h2>
            </div>
            <p className="text-sm text-slate-600 mb-6">Current employee compensation package</p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Annual Salary</label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-slate-500">$</span>
                  <input
                    type="text"
                    value={w2Salary}
                    onChange={(e) => setW2Salary(e.target.value.replace(/[^0-9]/g, ''))}
                    className="w-full pl-8 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Employer 401(k) Match (%)</label>
                <input
                  type="text"
                  value={w2401k}
                  onChange={(e) => setW2401k(e.target.value.replace(/[^0-9.]/g, ''))}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="5"
                />
                <p className="text-xs text-slate-500 mt-1">% of salary matched by employer</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Health Insurance Value (Annual)</label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-slate-500">$</span>
                  <input
                    type="text"
                    value={healthInsurance}
                    onChange={(e) => setHealthInsurance(e.target.value.replace(/[^0-9]/g, ''))}
                    className="w-full pl-8 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">Employer contribution to health coverage</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Paid Time Off (Days/Year)</label>
                <input
                  type="text"
                  value={paidTimeOff}
                  onChange={(e) => setPaidTimeOff(e.target.value.replace(/[^0-9]/g, ''))}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="15"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="ssMatch"
                  checked={employerSSMatch}
                  onChange={(e) => setEmployerSSMatch(e.target.checked)}
                  className="mr-2 h-4 w-4 text-primary-600"
                />
                <label htmlFor="ssMatch" className="text-sm text-slate-700">
                  Include employer payroll tax match (7.65%)
                </label>
              </div>
            </div>
          </div>

          {/* Freelance inputs */}
          <div className="bg-white rounded-3xl border-2 border-green-200 shadow-xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-green-100 to-emerald-200 rounded-xl">
                <svg className="w-7 h-7 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                  <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Freelance Scenario</h2>
                <p className="text-sm text-gray-600">Target income and expected costs</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Desired Take-Home Income</label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-slate-500">$</span>
                  <input
                    type="text"
                    value={desiredIncome}
                    onChange={(e) => setDesiredIncome(e.target.value.replace(/[^0-9]/g, ''))}
                    className="w-full pl-8 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">What you want to pocket after taxes</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Annual Business Expenses</label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-slate-500">$</span>
                  <input
                    type="text"
                    value={businessExpenses}
                    onChange={(e) => setBusinessExpenses(e.target.value.replace(/[^0-9]/g, ''))}
                    className="w-full pl-8 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">Software, equipment, home office, etc.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Health Insurance Cost (Annual)</label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-slate-500">$</span>
                  <input
                    type="text"
                    value={healthInsuranceFreelance}
                    onChange={(e) => setHealthInsuranceFreelance(e.target.value.replace(/[^0-9]/g, ''))}
                    className="w-full pl-8 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">Your total premium (deductible)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Retirement Contributions (Annual)</label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-slate-500">$</span>
                  <input
                    type="text"
                    value={retirementSavings}
                    onChange={(e) => setRetirementSavings(e.target.value.replace(/[^0-9]/g, ''))}
                    className="w-full pl-8 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">SEP IRA or Solo 401(k) target</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">State</label>
                <select
                  value={stateCode}
                  onChange={(e) => setStateCode(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="CA">California</option>
                  <option value="NY">New York</option>
                  <option value="TX">Texas (No income tax)</option>
                  <option value="FL">Florida (No income tax)</option>
                  <option value="WA">Washington (No income tax)</option>
                  <option value="OTHER">Other (5% estimate)</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="mt-10 bg-white rounded-3xl border-2 border-purple-200 shadow-2xl p-10">
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="p-3 bg-gradient-to-br from-purple-100 to-pink-200 rounded-xl">
              <svg className="w-8 h-8 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11 4a1 1 0 10-2 0v4a1 1 0 102 0V7zm-3 1a1 1 0 10-2 0v3a1 1 0 102 0V8zM8 9a1 1 0 00-2 0v2a1 1 0 102 0V9z" clipRule="evenodd" />
              </svg>
            </div>
            <h2 className="text-3xl font-black text-gray-900">Break-Even Analysis</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            {/* W-2 total */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border-2 border-blue-200 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                  <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
                </svg>
                Total W-2 Value
              </h3>
              <div className="space-y-2 text-sm mb-4">
                <div className="flex justify-between">
                  <span className="text-slate-600">Base Salary</span>
                  <span className="font-medium">${results.w2Breakdown.salary.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">401(k) Match</span>
                  <span className="font-medium">${results.w2Breakdown.retirement.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Health Insurance</span>
                  <span className="font-medium">${results.w2Breakdown.health.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Paid Time Off</span>
                  <span className="font-medium">${results.w2Breakdown.pto.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                </div>
                {employerSSMatch && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-slate-600">SS Match</span>
                      <span className="font-medium">${results.w2Breakdown.ssMatch.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Medicare Match</span>
                      <span className="font-medium">${results.w2Breakdown.medicareMatch.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                    </div>
                  </>
                )}
              </div>
              <div className="pt-4 border-t-2 border-blue-200">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-slate-900">Total Annual Value</span>
                  <span className="text-2xl font-bold text-blue-700">${results.totalW2Value.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                </div>
                <div className="text-xs text-slate-600 mt-1">
                  ${results.monthlyW2.toLocaleString(undefined, { maximumFractionDigits: 0 })}/month
                </div>
              </div>
            </div>

            {/* Freelance needed */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border-2 border-green-200 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                  <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
                </svg>
                Freelance Income Needed
              </h3>
              <div className="space-y-2 text-sm mb-4">
                <div className="flex justify-between">
                  <span className="text-slate-600">Desired Take-Home</span>
                  <span className="font-medium">${parseFloat(desiredIncome).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Business Expenses</span>
                  <span className="font-medium">${parseFloat(businessExpenses).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Est. Tax Rate</span>
                  <span className="font-medium">{(results.combinedTaxRate * 100).toFixed(1)}%</span>
                </div>
                <div className="text-xs text-slate-500 mt-2 pl-2 border-l-2 border-slate-300">
                  Self-employment (15.3%) + Federal (~22%) + State ({stateCode === "TX" || stateCode === "FL" || stateCode === "WA" ? "0%" : stateCode === "CA" ? "9.3%" : stateCode === "NY" ? "6.5%" : "5%"})
                </div>
              </div>
              <div className="pt-4 border-t-2 border-green-200">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-slate-900">Gross Revenue Needed</span>
                  <span className="text-2xl font-bold text-green-700">${results.totalFreelanceNeeded.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                </div>
                <div className="text-xs text-slate-600 mt-1">
                  ${results.monthlyFreelance.toLocaleString(undefined, { maximumFractionDigits: 0 })}/month
                </div>
              </div>
            </div>
          </div>

          {/* Verdict */}
          <div className={`rounded-3xl p-8 text-center border-2 shadow-xl ${results.freelanceAdvantage ? "bg-gradient-to-br from-green-50 to-emerald-50 border-green-300" : "bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-300"}`}>
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className={`p-3 rounded-full ${results.freelanceAdvantage ? "bg-green-600" : "bg-yellow-600"}`}>
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  {results.freelanceAdvantage ? (
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  ) : (
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  )}
                </svg>
              </div>
            </div>
            <h3 className="text-2xl font-black text-gray-900 mb-4">
              {results.freelanceAdvantage ? "✓ Freelancing is Financially Viable" : "⚠ You Need to Earn More as a Freelancer"}
            </h3>
            <p className="text-gray-700 mb-4 text-lg leading-relaxed">
              {results.freelanceAdvantage
                ? `To match your W-2 value, you need to earn $${results.totalFreelanceNeeded.toLocaleString(undefined, { maximumFractionDigits: 0 })}/year as a freelancer — that's $${results.difference.toLocaleString(undefined, { maximumFractionDigits: 0 })} less than your current total W-2 compensation.`
                : `To match your W-2 value, you need to earn $${results.totalFreelanceNeeded.toLocaleString(undefined, { maximumFractionDigits: 0 })}/year as a freelancer — that's $${results.difference.toLocaleString(undefined, { maximumFractionDigits: 0 })} more than your current total W-2 compensation.`}
            </p>
            <p className="text-sm text-gray-600 bg-white/50 rounded-xl p-4 border border-gray-200">
              <strong>Pro tip:</strong> This doesn't account for unpaid time off, inconsistent income, or finding your own clients.
              Many freelancers aim for 1.5-2x their W-2 salary to build a safety cushion.
            </p>
          </div>
        </div>

        {/* Educational content */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl border-2 border-blue-200 shadow-xl p-8 hover:shadow-2xl transition-shadow">
            <h3 className="font-black text-xl text-gray-900 mb-6 flex items-center gap-2">
              <div className="p-2 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                </svg>
              </div>
              Hidden W-2 Benefits
            </h3>
            <ul className="text-sm text-slate-700 space-y-2">
              <li>• Unemployment insurance eligibility</li>
              <li>• Workers' compensation coverage</li>
              <li>• Disability insurance (if offered)</li>
              <li>• Life insurance (if offered)</li>
              <li>• Professional development budget</li>
              <li>• Equipment and software provided</li>
            </ul>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl border-2 border-green-200 shadow-xl p-8 hover:shadow-2xl transition-shadow">
            <h3 className="font-black text-xl text-gray-900 mb-6 flex items-center gap-2">
              <div className="p-2 bg-gradient-to-br from-green-600 to-emerald-600 rounded-lg">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              Freelance Advantages
            </h3>
            <ul className="text-sm text-slate-700 space-y-2">
              <li>• Unlimited income potential</li>
              <li>• Tax deductions for business expenses</li>
              <li>• Location flexibility (work from anywhere)</li>
              <li>• Choose your own clients and projects</li>
              <li>• Control your schedule</li>
              <li>• Higher hourly rates typical</li>
            </ul>
          </div>

          <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl border-2 border-red-200 shadow-xl p-8 hover:shadow-2xl transition-shadow">
            <h3 className="font-black text-xl text-gray-900 mb-6 flex items-center gap-2">
              <div className="p-2 bg-gradient-to-br from-red-600 to-orange-600 rounded-lg">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              Risks to Consider
            </h3>
            <ul className="text-sm text-slate-700 space-y-2">
              <li>• Income variability (feast or famine)</li>
              <li>• Time spent finding clients</li>
              <li>• No paid sick days or vacation</li>
              <li>• Quarterly tax payments required</li>
              <li>• Self-funded retirement savings</li>
              <li>• Higher health insurance costs</li>
            </ul>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg p-8 text-white text-center">
          <h2 className="text-2xl font-bold mb-3">Ready to Calculate Your Freelance Taxes?</h2>
          <p className="mb-6 text-primary-50">
            Use our 1099 tax calculator to see exactly how much you'll owe in federal, state, and self-employment taxes
          </p>
          <Link
            to="/"
            className="inline-block bg-white text-primary-700 px-8 py-3 rounded-lg font-semibold hover:bg-primary-50 transition-colors"
          >
            Calculate My Taxes →
          </Link>
        </div>

        {/* Recent Posts */}
        <div className="mt-12">
          <RecentPosts />
        </div>
      </div>
    </div>
    </>
  );
}
